import React, { useState, useEffect, useRef } from 'react';
import MultiFormatReader from "./MultiFormatReader";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from 'reactstrap';
import { connect, useSelector } from 'react-redux'
import { addTeilnahmeQRReaderToggleMultiscanMode, addTeilnahmeQrCodeEFNScanned, beschriftungAusScan } from '../store/punkteMeldung/actions';
import { parseEFNEingabe, hatNamenstext } from '../store/punkteMeldung/efnBarcodeContentRecogniser';
import { istAbweichenderScan } from '../store/punkteMeldung/reducer';
import useDebouncedToast from './DebouncedToast';
import  moment  from 'moment';
import { CSS_CLASSNAME_PANEL } from '../EIVCommons';
import { getDeviceId, isLinearFormat, useWiederholungsBestaetigung } from './MultiFormatReaderUtils';
import { Howl } from 'howler';
import { MdCameraswitch } from 'react-icons/md';

const soundScanOk = new Howl({
  // src: ['./sounds/message-124468.mp3'] // Pfad zum Sound-File
  src: ['./sounds/camera-13695.mp3'] // Pfad zum Sound-File
});
const soundBereitsErfasst = new Howl({
  src: ['./sounds/rclick-13693.mp3'] // Pfad zum Sound-File
});
const soundScanNOk = new Howl({
  src: ['./sounds/stop-13692.mp3'] // Pfad zum Sound-File
});


const QRCodeScannerDialog = ({ isOpen, toggle, hideDialog, veranstaltung, applyScannedEFN, uebernehmeAusScan, multiscanMode, toggleMultiscanMode, addTeilnahmeState }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [showCamera, setShowCamera] = useState(true);
	const debouncedToast = useDebouncedToast(2000);
  const istBestaetigt = useWiederholungsBestaetigung();
  const erfassteTeilnahmen = useSelector((state) => state.punkteMeldung.erfassteTeilnahmen[veranstaltung.vnr]);
  const beschriftungen = useSelector((state) => state.punkteMeldung.beschriftungen[veranstaltung.vnr]);
  // console.log(veranstaltung);

  let multiscan = useRef(multiscanMode);
  let erfassteT = useRef(erfassteTeilnahmen);
  erfassteT.current = erfassteTeilnahmen?erfassteTeilnahmen:[];
  let beschriftungenRef = useRef(beschriftungen);
  beschriftungenRef.current = beschriftungen?beschriftungen:{};
  useEffect(() => {
    const fetchDevices = async () => {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = mediaDevices.filter((device) => device.kind === 'videoinput');
      setDevices(videoDevices);
      if (videoDevices.length > 0) {
        try{
          const deviceId = await getDeviceId(mediaDevices, facingMode);
          setSelectedDevice(deviceId);
        }catch(e){
          setSelectedDevice(videoDevices[0].deviceId);
        }
      }
    };

    fetchDevices();
  }, [facingMode]);
  
  useEffect(() => {
    // console.log("multiscanMode hat sich geändert:", multiscanMode);
    multiscan.current = multiscanMode;
  }, [multiscanMode]);

  useEffect(() => {
    // console.log("erfassteTeilnahmen hat sich geändert:", JSON.stringify(erfassteTeilnahmen));
    erfassteT.current = erfassteTeilnahmen;
  }, [erfassteTeilnahmen]);

  useEffect(() => {
    beschriftungenRef.current = beschriftungen;
  }, [beschriftungen]);
  
  // console.log("------------> multiscanMode %s", multiscanMode);

  /** Nach jedem Treffer im Einzel-Modus schliessen, im Multiscan offen lassen. */
  const schliesseImEinzelModus = () => { if (!multiscan.current) toggle(); };

  // hatNamenstext, nicht hatBeifang: ein QR-Code mit EFN und blosser E-Mail-Adresse traegt zwar
  // Beifang (und bleibt eine gueltige Teilnahme), aber keinen Namen. Mit dem falschen Praedikat
  // stuende hier "EFN: ... () erfasst" mit leeren Klammern, und eine bereits erfasste EFN bekaeme
  // ein "ergaenzt", dem im Store nichts entspricht - der Reducer verwirft eine leere Beschriftung.
  const beschriftungAlsText = (beschriftung) => (beschriftung.text || '').trim();

  const handleScan = (data) => {
    const text = typeof data?.text === 'string' ? data.text.trim() : '';
    if (!text) {
      return;
    }
    // Der Barcode traegt oft mehr als die EFN: die Teilnehmer-App der LAEKH haengt auf Wunsch
    // die Anrede an, andere Apps packen Name und E-Mail dazu. Was sich davon als Beschriftung
    // verwenden laesst, wird hier gleich mit uebernommen - siehe efnBarcodeContentRecogniser.
    const geparst = parseEFNEingabe(text);
    const efn = geparst.efn;
    // Eine gültige EFN trägt ihre Prüfung selbst (Länge, Präfix, Luhn) und wird
    // sofort übernommen. Ein QR-Code mit falschem Inhalt ebenfalls sofort
    // gemeldet - er kann kurz hingehalten worden sein, und QR halluziniert nicht.
    // Nur bei Strichcodes auf die Wiederholung warten, siehe
    // useWiederholungsBestaetigung.
    if (!efn && isLinearFormat(data.format) && !istBestaetigt(text)) {
      console.debug('unbestätigter 1D-Treffer, warte auf Wiederholung: %s', text);
      return;
    }
    if (!efn) {
      debouncedToast(`keine EFN erkannt`, "warning", () => soundScanNOk.play());
      return;
    }
    if (!erfassteT.current[efn]) {
      applyScannedEFN(efn, addTeilnahmeState, geparst);
      const zusatz = hatNamenstext(geparst) ? ` (${beschriftungAlsText(geparst)})` : '';
      debouncedToast(`EFN: ${efn}${zusatz} erfasst`, "success", () => soundScanOk.play());
      schliesseImEinzelModus();
      return;
    }
    const vorhandeneBeschriftung = beschriftungenRef.current[efn];
    // Schon erfasst, aber noch ohne Namen: dann traegt dieser Scan ihn nach. So laesst sich
    // eine bestehende Liste im Multiscan-Modus nachtraeglich anreichern.
    if (hatNamenstext(geparst) && !hatNamenstext(vorhandeneBeschriftung)) {
      uebernehmeAusScan(efn, geparst);
      debouncedToast(`EFN: ${efn} – ${beschriftungAlsText(geparst)} ergänzt`, "success", () => soundScanOk.play());
      schliesseImEinzelModus();
      return;
    }
    // Der Barcode nennt einen anderen Namen als der, der hier steht. Nicht ueberschreiben und
    // nicht wegwerfen: der Widerspruch wird gemerkt und in der Zeile aufgeloest. Kein Dialog -
    // am Einlass darf nichts den Scanvorgang anhalten.
    if (istAbweichenderScan(vorhandeneBeschriftung, geparst)) {
      uebernehmeAusScan(efn, geparst);
      debouncedToast(`EFN: ${efn} steht als „${vorhandeneBeschriftung.text}“ – Barcode nennt „${geparst.text}“`,
        "warning", () => soundBereitsErfasst.play());
      schliesseImEinzelModus();
      return;
    }
    debouncedToast(`EFN: ${efn} bereits erfasst`, "info", () => soundBereitsErfasst.play());
    schliesseImEinzelModus();
  };
  const handleToggleMode = () => {
    toggleMultiscanMode();
  };

  const refreshCameraView = () => {
    setShowCamera(false); setTimeout( () => setShowCamera(true), 30);
  }


  const toggleFacingMode = () => {
    setFacingMode(facingMode === 'user' ? 'environment' : 'user');
    refreshCameraView();
  };
  
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
      Teilnehmer mit EFN erfassen 
      </ModalHeader>
      <ModalBody>
        <small>
          <VeranstaltungsItem small veranstaltung={veranstaltung} />
        </small>

        {/* Zustand und Umschalter in einem Bedienelement: vorher standen ein Hinweisfeld
            und ein Umriss-Knopf untereinander und sahen im Dunkelmodus fast gleich aus -
            man konnte nicht sehen, welches der beiden das Bedienelement ist. */}
        <div className="mb-3">
          <div className="btn-group w-100" role="group" aria-label="Scan-Modus">
            <button type="button"
              className={"btn " + (multiscanMode ? "btn-outline-primary" : "btn-primary")}
              aria-pressed={!multiscanMode}
              onClick={() => { if (multiscanMode) handleToggleMode(); }}>Einzel-Modus</button>
            <button type="button"
              className={"btn " + (multiscanMode ? "btn-primary" : "btn-outline-primary")}
              aria-pressed={multiscanMode}
              onClick={() => { if (!multiscanMode) handleToggleMode(); }}>Multiscan-Modus</button>
          </div>
          <small className="text-body-secondary d-block mt-2">
            {multiscanMode
              ? "Mehrere EFNs nacheinander scannen. Der Dialog bleibt offen, bis Sie ihn schließen."
              : "Eine EFN scannen. Der Dialog schließt sich nach dem Treffer."}
          </small>
        </div>

        { showCamera &&
          <MultiFormatReader
            onScan={handleScan}
            constraints={{ deviceId: selectedDevice, facingMode }}
          />
        }
        {devices.length > 1 && (
          <FormGroup className="mt-3 mb-0">
            <Label for="deviceSelect" className="small text-body-secondary mb-1">Kamera auswählen</Label>
            <Input
              type="select"
              name="deviceSelect"
              bsSize="sm"
              defaultValue={selectedDevice}
              id="deviceSelect"
              onChange={(e) => {
                setSelectedDevice(e.target.value); refreshCameraView();
                }}
            >
              {devices.map((device, index) => (
                <option key={index} value={device.deviceId}>{device.label || `Kamera ${index + 1}`}</option>
              ))}
            </Input>
          </FormGroup>
        )}
        {/* Die Kamerawahl gehört zum Sucher, nicht in die Fußzeile: dort stand sie als
            breiter blauer Knopf über dem kleinen "Schließen" und wirkte wie die
            Hauptaktion des Dialogs. */}
        <div className="d-flex justify-content-end mt-2">
          <Button color="secondary" size="sm" outline onClick={toggleFacingMode}>
            <MdCameraswitch className="me-1" size="1.15em" />Front-/Rückkamera wechseln
          </Button>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline className="w-100" onClick={toggle}>Schließen</Button>
      </ModalFooter>
    </Modal>
  );
};
const mapStateToProps = (state, ownProps) => { 
	return {
		multiscanMode: state.punkteMeldung.qrReader.multiscanMode,
    addTeilnahmeState: state.punkteMeldung.addTeilnahme[ownProps.veranstaltung.vnr]
	}
}
function VeranstaltungsItem(props){
	const {veranstaltung, small} = props;
  const themaClassName=small?"fw-bold":"h5";
	const endeZeit = moment(veranstaltung.beginn).format('DD.MM.YYYY') !== moment(veranstaltung.ende).format('DD.MM.YYYY')?
		moment(veranstaltung.ende).format('DD.MM.YYYY HH:mm'):moment(veranstaltung.ende).format('HH:mm')
	return (<div className={`clickableValue container-fluid ${CSS_CLASSNAME_PANEL}`}>
			<div>{veranstaltung.vnr}</div>
			<div className={themaClassName}>{veranstaltung.thema}</div>
			<div>{veranstaltung.unterthema}</div>
			<div className="row text-muted ">
			<div className="col-12 col-sm-7 col-md-6 col-lg-8">{moment(veranstaltung.beginn).format('DD.MM.YYYY HH:mm')} - {endeZeit}</div>
</div>
	</div>);

}
const mapDispatchToProp = (dispatch, ownProps) => ({
  applyScannedEFN: (qrScanResult, addTeilnahmeState, beschriftung) => dispatch(addTeilnahmeQrCodeEFNScanned(ownProps.veranstaltung.vnr, qrScanResult, addTeilnahmeState, beschriftung)),
  uebernehmeAusScan: (efn, beschriftung) => dispatch(beschriftungAusScan(ownProps.veranstaltung.vnr, efn, beschriftung)),
	toggleMultiscanMode: () => dispatch(addTeilnahmeQRReaderToggleMultiscanMode()),
	
})

export default connect(mapStateToProps, mapDispatchToProp)(QRCodeScannerDialog);
